import { EmberEquipment, emberEquipmentKey, emberEquipmentOf, planEmberAnimation } from "game-common/ember_sprite/generate"
import { Frame } from "game-common/ember_sprite/raster"
import { Entity } from "game-common/models"

import { claimHeavyWork } from "../../frame_work_budget"
import { Facing, copyDraft, spriteOf } from "./creation_sheet"
import { TraitId, applyTraitChoice } from "./traits"

// The small pictures the panel shows for each hairstyle, each beard and each cut of shirt, legs and feet:
// the player's own character with that one option on, cropped to the part it changes.
//
// They are rendered here, from the same generator the game draws bodies with, rather than through the
// shared frame baker: the baker keeps every look it has baked for the whole session, and the hair grid
// alone is seventy-odd looks for every hair colour tried. These crops are 12 x 12 and live in a bounded
// cache the page drops when it closes.

export type PictureTrait = Extract<TraitId, "hair" | "beard" | "shirt" | "pants" | "shoes">

/** A picture's side, in art pixels: one pixel of the body is one pixel of the picture, never scaled. */
export const PICTURE = 12

/**
 * Hair, beards and shoes read best three-quarters on (a mustache and a beard's length both show); a
 * shirt's and legs' cut reads best from the front.
 */
export const PICTURE_FACING: Record<PictureTrait, Facing> = {
    hair: "frontright",
    beard: "frontright",
    shirt: "front",
    pants: "front",
    shoes: "frontright",
}

export interface PictureSpec {
    trait: PictureTrait
    value: string
    sprite: string
    facing: Facing
    equipment: EmberEquipment
    /** Everything the picture's pixels depend on. */
    key: string
}

/**
 * What a picture of `value` shows: the draft with only that option changed. Feet pictures go bare-legged
 * to the knee -- a shoe is a few pixels under a trouser hem, and the picture is about the shoe.
 */
export const pictureSpec = (draft: Entity, trait: PictureTrait, value: string): PictureSpec => {
    const tried = copyDraft(draft)
    applyTraitChoice(tried, trait, "style", value)
    const equipment = emberEquipmentOf(tried)
    if (trait === "shoes") {
        delete equipment.pants
    }
    const sprite = spriteOf(tried)
    const facing = PICTURE_FACING[trait]
    return { trait, value, sprite, facing, equipment, key: `${trait}|${sprite}|${facing}|${emberEquipmentKey(equipment)}` }
}

export type RenderFrame = (sprite: string, facing: Facing, equipment: EmberEquipment) => Frame

/** One standing frame of a body, from the game's own generator. */
export const renderIdle: RenderFrame = (sprite, facing, equipment) =>
    planEmberAnimation(sprite, `idle-${facing}`, equipment).renderFrame(0)

/** The box round a frame's drawn pixels, and the centre of a band of its rows. */
const opaque = (frame: Frame, x: number, y: number) => frame.pixels[(y * frame.width + x) * 4 + 3] > 0

const rowsOf = (frame: Frame): { top: number; bottom: number } => {
    let top = frame.height
    let bottom = -1
    for (let y = 0; y < frame.height; y++) {
        for (let x = 0; x < frame.width; x++) {
            if (opaque(frame, x, y)) {
                top = Math.min(top, y)
                bottom = Math.max(bottom, y)
                break
            }
        }
    }
    return { top, bottom }
}

const centreOf = (frame: Frame, y0: number, y1: number): number => {
    let sum = 0
    let n = 0
    for (let y = Math.max(0, y0); y < Math.min(frame.height, y1); y++) {
        for (let x = 0; x < frame.width; x++) {
            if (opaque(frame, x, y)) {
                sum += x
                n++
            }
        }
    }
    return n ? Math.round(sum / n) : Math.round(frame.width / 2)
}

export interface CropWindow {
    /** The crop's top-left pixel in the frame. */
    x: number
    y: number
}

/**
 * Where each trait's picture sits on a body, measured on its bald, bare body so every option of a trait
 * is cropped through the same window: the head with room above for hair, the face down to the chest for a
 * beard, the chest, the legs from mid
 * thigh to mid shin, the feet standing on the picture's floor.
 */
export const cropWindow = (bare: Frame, trait: PictureTrait): CropWindow => {
    const { top, bottom } = rowsOf(bare)
    const height = bottom - top
    const half = PICTURE / 2
    switch (trait) {
        case "hair":
            return { x: centreOf(bare, top, top + 6) - half, y: top - 3 }
        case "beard":
            return { x: centreOf(bare, top, top + 6) - half, y: top + 2 }
        case "shirt":
            return { x: centreOf(bare, top, bottom) - half, y: top + Math.round(0.2 * height) }
        case "pants":
            return { x: centreOf(bare, top, bottom) - half, y: top + Math.round(0.64 * height) }
        default:
            return { x: centreOf(bare, bottom - 3, bottom + 1) - half, y: bottom - (PICTURE - 3) }
    }
}

/** A frame's pixels through a window, as packed colours (the PixelBuffer layout). */
export const cropFrame = (frame: Frame, window: CropWindow): Uint32Array => {
    const out = new Uint32Array(PICTURE * PICTURE)
    const source = new Uint32Array(frame.pixels.buffer, frame.pixels.byteOffset, frame.width * frame.height)
    for (let y = 0; y < PICTURE; y++) {
        for (let x = 0; x < PICTURE; x++) {
            const fx = window.x + x
            const fy = window.y + y
            if (fx >= 0 && fy >= 0 && fx < frame.width && fy < frame.height) {
                out[y * PICTURE + x] = source[fy * frame.width + fx]
            }
        }
    }
    return out
}

export interface PicturesOptions {
    render?: RenderFrame
    now?: () => number
    /** Whether this frame's turn for heavy work is free; see frame_work_budget. */
    claim?: () => boolean
    budgetMs?: number
    capacity?: number
}

/**
 * The panel's pictures: rendered on demand, a few a frame, and kept. A picture asked for again while its
 * new look is still queued keeps showing the one it had, so the panel never blinks empty.
 */
export class Pictures {
    private readonly render: RenderFrame
    private readonly now: () => number
    private readonly claim: () => boolean
    private readonly budgetMs: number
    private readonly capacity: number
    // Least recently used first: a Map keeps insertion order, and a hit is re-inserted.
    private readonly crops = new Map<string, Uint32Array>()
    // What each option's slot last showed, by trait and value.
    private readonly shown = new Map<string, Uint32Array>()
    private readonly windows = new Map<string, CropWindow>()
    private queue: PictureSpec[] = []
    rendered = 0

    constructor({ render = renderIdle, now, claim = claimHeavyWork, budgetMs = 4, capacity = 512 }: PicturesOptions = {}) {
        this.render = render
        this.now = now ?? (() => (typeof performance !== "undefined" ? performance.now() : Date.now()))
        this.claim = claim
        this.budgetMs = budgetMs
        this.capacity = capacity
    }

    /** The picture for `spec` as it can be shown now, queueing a render if its look isn't ready. */
    get = (spec: PictureSpec, priority: boolean = false): Uint32Array | undefined => {
        const slot = `${spec.trait}:${spec.value}`
        const crop = this.crops.get(spec.key)
        if (crop) {
            this.crops.delete(spec.key)
            this.crops.set(spec.key, crop)
            this.shown.set(slot, crop)
            return crop
        }
        const queued = this.queue.findIndex(item => item.key === spec.key)
        if (queued >= 0 && priority) {
            this.queue.splice(queued, 1)
        }
        if (queued < 0 || priority) {
            if (priority) this.queue.unshift(spec)
            else this.queue.push(spec)
        }
        return this.shown.get(slot)
    }

    get pending(): number {
        return this.queue.length
    }

    /** Render queued pictures while this frame's budget lasts. Returns how many landed. */
    work = (): number => {
        if (!this.queue.length || !this.claim()) {
            return 0
        }
        const start = this.now()
        let landed = 0
        while (this.queue.length && (landed === 0 || this.now() - start < this.budgetMs)) {
            const spec = this.queue.shift()
            if (this.crops.has(spec.key)) {
                continue
            }
            const crop = cropFrame(this.render(spec.sprite, spec.facing, spec.equipment), this.windowFor(spec))
            this.crops.set(spec.key, crop)
            this.shown.set(`${spec.trait}:${spec.value}`, crop)
            this.rendered++
            landed++
            while (this.crops.size > this.capacity) {
                this.crops.delete(this.crops.keys().next().value)
            }
        }
        return landed
    }

    private windowFor = (spec: PictureSpec): CropWindow => {
        const key = `${spec.trait}|${spec.sprite}|${spec.facing}`
        let window = this.windows.get(key)
        if (!window) {
            window = cropWindow(this.render(spec.sprite, spec.facing, { hair: "bald" }), spec.trait)
            this.windows.set(key, window)
        }
        return window
    }

    clear = () => {
        this.crops.clear()
        this.shown.clear()
        this.windows.clear()
        this.queue = []
    }
}
